import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useIsMobile } from "@/components/shared/PagePrimitives";
import SuperadminPipeline from "@/components/superadmin/Pipeline";
import CompanyPanel from "@/components/superadmin/CompanyPanel";
import { useFirmActions } from "@/components/superadmin/FirmActions";
import { FakturoidDialog } from "@/components/superadmin/Subscription";
import { SEGMENTS, COLUMNS, segOf, colOf, sortFor } from "@/components/superadmin/firm";

// Superadmin (209) — varianta Pipeline. Data drží react-query: návrat na stránku ukáže
// poslední stav hned a obnoví ho na pozadí. Detail firmy = panel zprava s vlastní URL
// (?firma=…), takže funguje sdílený odkaz i tlačítko Zpět (zavře panel).

const get = (url) => api.get(url).then((r) => r.data);

export default function Superadmin() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const selId = params.get("firma");
  const [panelTab, setPanelTab] = useState("prehled");
  const [faOpen, setFaOpen] = useState(false);
  const pushed = useRef(false);

  const overview = useQuery({ queryKey: ["sa", "overview"], queryFn: () => get("/admin/overview"), staleTime: 30_000 });
  const companies = useQuery({ queryKey: ["sa", "companies"], queryFn: () => get("/admin/companies"), staleTime: 30_000 });
  const fa = useQuery({ queryKey: ["sa", "fakturoid"], queryFn: () => get("/admin/fakturoid"), staleTime: 300_000 });
  const reload = () => qc.invalidateQueries({ queryKey: ["sa"] });
  const actions = useFirmActions({ kpi: overview.data, reload });
  const rows = useMemo(() => companies.data || [], [companies.data]);

  useEffect(() => { if (!selId) pushed.current = false; }, [selId]);
  const open = (id, tab) => {
    setPanelTab(tab || "prehled");
    if (id === selId) return;
    pushed.current = true;
    setParams({ firma: id });
  };
  const navigate = (id) => setParams({ firma: id }, { replace: true });
  const close = () => {
    if (pushed.current) { pushed.current = false; nav(-1); }
    else setParams({}, { replace: true });
  };

  // Šipky v panelu: firmy ze stejného sloupce tabule (u ostatních stavů ze stejného segmentu).
  const row = rows.find((r) => r.id === selId) || null;
  const { siblings, label } = useMemo(() => {
    if (!row) return { siblings: null, label: "" };
    const seg = segOf(row), col = seg === "aktivni" ? colOf(row) : null;
    const key = col || seg;
    const list = rows.filter((r) => segOf(r) === seg && (!col || colOf(r) === col)).sort(sortFor(key));
    return { siblings: list.map((r) => r.id), label: Object.fromEntries([...COLUMNS, ...SEGMENTS])[key] };
  }, [rows, row]);

  return (
    <>
      <SuperadminPipeline kpi={overview.data} rows={rows} loading={companies.isPending} fa={fa.data}
        onFakturoid={() => setFaOpen(true)} onOpen={open} selectedId={selId} run={actions.run} busy={actions.busy} />
      <CompanyPanel id={selId} row={row} siblings={siblings} label={label} kpi={overview.data} fa={fa.data} isMobile={isMobile}
        onClose={close} onNavigate={navigate} reload={reload} run={actions.run} busy={actions.busy} initialTab={panelTab} />
      {actions.dialogs}
      <FakturoidDialog open={faOpen} onOpenChange={(o) => { setFaOpen(o); if (!o) qc.invalidateQueries({ queryKey: ["sa", "fakturoid"] }); }} />
    </>
  );
}
